import axios from "axios";

import { endpoints } from "../../../api/endpoints";

export type ReportType = "platform" | "moderation" | "user-detail" | "qr";

export type ReportDateRange = {
  from?: string;
  to?: string;
};

export const REPORT_ORDER: ReportType[] = ["platform", "moderation", "user-detail", "qr"];

const REPORT_ENDPOINTS: Record<ReportType, string> = {
  platform: endpoints.admin.getCSVReport,
  moderation: endpoints.admin.getModerationCSV,
  "user-detail": endpoints.admin.getUserDetailCSV,
  qr: endpoints.admin.getQRAnalyticsCSV,
};

const REPORT_CSV_FILENAMES: Record<ReportType, string> = {
  platform: "platform-report.csv",
  moderation: "moderation-actions.csv",
  "user-detail": "user-detail-report.csv",
  qr: "qr-analytics.csv",
};

const REPORT_SHEET_NAMES: Record<ReportType, string> = {
  platform: "Platform",
  moderation: "Moderation",
  "user-detail": "User detail",
  qr: "QR",
};

const withDateParams = (params?: ReportDateRange) => {
  const query: Record<string, string> = {};
  if (params?.from) query.from = params.from;
  if (params?.to) query.to = params.to;
  return query;
};

const triggerBlobDownload = (data: BlobPart, filename: string, mimeType?: string) => {
  const url = window.URL.createObjectURL(new Blob([data], mimeType ? { type: mimeType } : undefined));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const readBlobErrorMessage = async (blob: Blob): Promise<string> => {
  const text = await blob.text();
  try {
    const json = JSON.parse(text) as { message?: string };
    return json.message || text;
  } catch {
    return text || "Download failed";
  }
};

const fetchReportBlob = async (type: ReportType, range?: ReportDateRange): Promise<Blob> => {
  try {
    const response = await axios.get(REPORT_ENDPOINTS[type], {
      responseType: "blob",
      params: withDateParams(range),
    });

    const blob = response.data as Blob;
    const contentType = String(response.headers["content-type"] || blob.type || "");

    if (contentType.includes("json") || contentType.includes("html")) {
      throw new Error(await readBlobErrorMessage(blob));
    }

    return blob;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      throw new Error(await readBlobErrorMessage(error.response.data));
    }
    throw error;
  }
};

const buildWorkbookFilename = (range?: ReportDateRange): string => {
  if (range?.from && range?.to) {
    return `werule-reports-${range.from}-to-${range.to}.xlsx`;
  }
  return "werule-reports-all-time.xlsx";
};

const parseCsvText = (Papa: { parse: typeof import("papaparse").parse }, text: string): string[][] => {
  const parsed = Papa.parse<string[]>(text, {
    skipEmptyLines: false,
    delimiter: ",",
    quoteChar: '"',
  });

  const blockingErrors = parsed.errors.filter((entry) => entry.code !== "UndetectableDelimiter");
  if (blockingErrors.length > 0) {
    throw new Error(blockingErrors[0]?.message || "Failed to parse CSV");
  }

  return parsed.data;
};

const buildWorkbookFromReports = async (
  ordered: ReportType[],
  blobs: Blob[],
  range?: ReportDateRange
): Promise<void> => {
  const [{ default: ExcelJS }, { default: Papa }] = await Promise.all([
    import("exceljs"),
    import("papaparse"),
  ]);

  const workbook = new ExcelJS.Workbook();

  for (let index = 0; index < ordered.length; index += 1) {
    const type = ordered[index];
    const text = await blobs[index].text();
    const rows = parseCsvText(Papa, text);
    const sheet = workbook.addWorksheet(REPORT_SHEET_NAMES[type]);
    rows.forEach((row) => sheet.addRow(row));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerBlobDownload(
    buffer,
    buildWorkbookFilename(range),
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  );
};

export const downloadSelectedReports = async (
  selected: ReportType[],
  range?: ReportDateRange
): Promise<void> => {
  if (selected.length === 0) {
    return;
  }

  const ordered = REPORT_ORDER.filter((type) => selected.includes(type));

  if (ordered.length === 1) {
    const type = ordered[0];
    const blob = await fetchReportBlob(type, range);
    triggerBlobDownload(blob, REPORT_CSV_FILENAMES[type], "text/csv");
    return;
  }

  const blobs = await Promise.all(ordered.map((type) => fetchReportBlob(type, range)));
  await buildWorkbookFromReports(ordered, blobs, range);
};
